import type { ChildFamilyName } from './valueObjects/childFamilyName';
import type { ChildFamilyNameKana } from './valueObjects/childFamilyNameKana';
import type { ChildId } from './valueObjects/childId';
import type { ChildName } from './valueObjects/childName';
import type { ChildNameKana } from './valueObjects/childNameKana';
import type { FacilityId } from './valueObjects/facilityId';
import type { ParentId } from './valueObjects/parentId';
import type { RemoveDateTime } from './valueObjects/removeDateTime';
import type { ChildRemoveJsonPub, ChildRemoveJsonPvt } from '@/types/domain/childApply/entities/childRemoveEntity';

import { EntityPub } from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 退園プロパティ非公開
 */
export type ChildRemovePropsPvt = {
  /** 親ID */
  parentId: ParentId,
  /** 子どもID */
  childId: ChildId,
  /** 子どもの名前 */
  childName: ChildName,
  /** 子どもの苗字 */
  childFamilyName: ChildFamilyName,
  /** 子どもの名前かな */
  childNameKana: ChildNameKana,
  /** 子ども苗字かな */
  childFamilyNameKana: ChildFamilyNameKana,
  /** 施設ID */
  facilityId: FacilityId,
  /** 退園日時 */
  removeDateTime: RemoveDateTime,
}

/**
 * 退園プロパティ公開
 */
export type ChildRemovePropsPub = Record<string, never>

/**
 * 退園Entity
 */
export class ChildRemoveEntity extends EntityPub<
  ChildRemovePropsPvt,
  ChildRemoveJsonPvt,
  ChildRemovePropsPub,
  ChildRemoveJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  get deleteKey(): string {
    return this.getFutureMonth(this.removeDateTime.value, 1).toJSON();
  }

  toJsonData(): ChildRemoveJsonPvt {
    return {
      childId: this.childId.value,
      childName: this.childName.value,
      childNameKana: this.childNameKana.value,
      childFamilyName: this.childFamilyName.value,
      childFamilyNameKana: this.childFamilyNameKana.value,
      facilityId: this.facilityId.value,
      parentId: this.parentId.value,
      removeDateTime: this.removeDateTime.value.toJSON(),
    };
  }

  toJsonPubData(): ChildRemoveJsonPub {
    return {};
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryChildApplyChildRemoveEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.removeDateTime.toStringDateTime()}#${this.facilityId.value}#${this.parentId.value}_${this.childId.value}`;
  }

  /**
   * 子どもID
   */
  get childId(): ChildId {
    return this._value.childId;
  }

  /**
   * 子どもの名前
   */
  get childName(): ChildName {
    return this._value.childName;
  }

  /**
   * 子どもの名前
   */
  set childName(childName: ChildName) {
    this._value.childName = childName;
  }

  /**
   * 子どもの名前かな
   */
  get childNameKana(): ChildNameKana {
    return this._value.childNameKana;
  }

  /**
   * 子どもの名前かな
   */
  set childNameKana(childNameKana: ChildNameKana) {
    this._value.childNameKana = childNameKana;
  }

  /**
   * 子どもの苗字
   */
  get childFamilyName(): ChildFamilyName {
    return this._value.childFamilyName;
  }

  /**
   * 子どもの苗字
   */
  set childFamilyName(childFamilyName: ChildFamilyName) {
    this._value.childFamilyName = childFamilyName;
  }

  /**
   * 子どもの苗字かな
   */
  get childFamilyNameKana(): ChildFamilyNameKana {
    return this._value.childFamilyNameKana;
  }

  /**
   * 子どもの苗字かな
   */
  set childFamilyNameKana(childFamilyNameKana: ChildFamilyNameKana) {
    this._value.childFamilyNameKana = childFamilyNameKana;
  }

  /**
   * 親ID
   */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /**
   * 施設ID
   */
  get facilityId(): FacilityId {
    return this._value.facilityId;
  }

  /**
   * 施設ID
   */
  set facilityId(facilityId: FacilityId) {
    this._value.facilityId = facilityId;
  }

  /**
   * 退園日時
   */
  get removeDateTime(): RemoveDateTime {
    return this._value.removeDateTime;
  }

  /**
   * 退園日時
   */
  set removeDateTime(removeDateTime: RemoveDateTime) {
    this._value.removeDateTime = removeDateTime;
  }
}
